import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'

const mp = (path) => client.get(`/marketplace${path}`)
const mpPost = (path, data) => client.post(`/marketplace${path}`, data)
const mpPut = (path, data) => client.put(`/marketplace${path}`, data)
const mpPatch = (path, data) => client.patch(`/marketplace${path}`, data)
const mpDel = (path) => client.delete(`/marketplace${path}`)

export const useMarketplaceProductStore = defineStore('marketplaceProduct', () => {
  const product = ref(null)
  const products = ref([])
  const loading = ref(false)
  const error = ref(null)

  async function create(data) {
    loading.value = true
    try {
      const { data: result } = await mpPost('/products', data)
      product.value = result
      return result
    } catch (e) { error.value = e.message; throw e } finally { loading.value = false }
  }

  async function get(productId) {
    loading.value = true
    try {
      const { data } = await mp(`/products/${productId}`)
      product.value = data
      return data
    } catch (e) { error.value = e.message; throw e } finally { loading.value = false }
  }

  async function getByListing(listingId) {
    try { const { data } = await mp(`/products/listing/${listingId}`); return data } catch { return null }
  }

  async function getByShop(shopId) {
    try { const { data } = await mp(`/products/shop/${shopId}`); products.value = data; return data } catch { return [] }
  }

  async function getActiveByShop(shopId) {
    try { const { data } = await mp(`/products/shop/${shopId}/active`); products.value = data; return data } catch { return [] }
  }

  async function update(productId, data) {
    try { const { data: result } = await mpPut(`/products/${productId}`, data); product.value = result; return result } catch (e) { error.value = e.message; throw e }
  }

  async function updatePartial(productId, data) {
    try { const { data: result } = await mpPatch(`/products/${productId}`, data); product.value = result; return result } catch (e) { error.value = e.message; throw e }
  }

  async function remove(productId) {
    try { await mpDel(`/products/${productId}`); product.value = null } catch (e) { error.value = e.message; throw e }
  }

  async function activate(productId) {
    try { await mpPost(`/products/${productId}/activate`) } catch (e) { error.value = e.message; throw e }
  }

  async function pause(productId) {
    try { await mpPost(`/products/${productId}/pause`) } catch (e) { error.value = e.message; throw e }
  }

  async function archive(productId) {
    try { await mpPost(`/products/${productId}/archive`) } catch (e) { error.value = e.message; throw e }
  }

  // ─── Media ──────────────────────────────────────────────────────

  async function addMedia(productId, data) {
    try { const { data: result } = await mpPost(`/products/${productId}/media`, data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function listMedia(productId) {
    try { const { data } = await mp(`/products/${productId}/media`); return data } catch { return [] }
  }

  async function removeMedia(mediaId) {
    try { await mpDel(`/products/media/${mediaId}`) } catch (e) { error.value = e.message; throw e }
  }

  // ─── Variants ───────────────────────────────────────────────────

  async function createVariant(productId, data) {
    try { const { data: result } = await mpPost(`/products/${productId}/variants`, data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function listVariants(productId) {
    try { const { data } = await mp(`/products/${productId}/variants`); return data } catch { return [] }
  }

  async function updateVariant(variantId, data) {
    try { const { data: result } = await mpPut(`/products/variants/${variantId}`, data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function removeVariant(variantId) {
    try { await mpDel(`/products/variants/${variantId}`) } catch (e) { error.value = e.message; throw e }
  }

  // ─── Price Rules ────────────────────────────────────────────────

  async function createPriceRule(productId, data) {
    try { const { data: result } = await mpPost(`/products/${productId}/price-rules`, data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function listPriceRules(productId) {
    try { const { data } = await mp(`/products/${productId}/price-rules`); return data } catch { return [] }
  }

  async function updatePriceRule(ruleId, data) {
    try { const { data: result } = await mpPut(`/products/price-rules/${ruleId}`, data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function removePriceRule(ruleId) {
    try { await mpDel(`/products/price-rules/${ruleId}`) } catch (e) { error.value = e.message; throw e }
  }

  // ─── Seller Profile ───────────────────────────────────────────────

  async function sellerProfile(data) {
    try { const { data: result } = await mpPost('/seller-profile', data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function myListings() {
    try { const { data } = await mp('/my-listings'); return data } catch (e) { error.value = e.message; throw e }
  }

  // ─── Listings ─────────────────────────────────────────────────────

  async function createListing(payload) {
    try { const { data } = await mpPost('/listings', payload); return data } catch (e) { error.value = e.message; throw e }
  }

  async function updateListing(listingId, payload) {
    try { const { data } = await mpPatch(`/listings/${listingId}`, payload); return data } catch (e) { error.value = e.message; throw e }
  }

  // ─── Listing Submissions ──────────────────────────────────────────

  async function submitListing(listingId) {
    try { const { data } = await mpPost(`/listings/${listingId}/submit`); return data } catch (e) { error.value = e.message; throw e }
  }

  // ─── Group Marketplace Listings ───────────────────────────────────

  async function updateGroupMarketplaceListing(groupId, groupListingId, request) {
    try { const { data } = await mpPatch(`/groups/${groupId}/listings/${groupListingId}`, request); return data } catch (e) { error.value = e.message; throw e }
  }

  // ─── Feature Toggles ────────────────────────────────────────────

  async function enableNegotiation(productId) {
    try { await mpPost(`/products/${productId}/negotiation/enable`) } catch (e) { error.value = e.message; throw e }
  }

  async function disableNegotiation(productId) {
    try { await mpPost(`/products/${productId}/negotiation/disable`) } catch (e) { error.value = e.message; throw e }
  }

  async function enableGroupBuy(productId) {
    try { await mpPost(`/products/${productId}/group-buy/enable`) } catch (e) { error.value = e.message; throw e }
  }

  async function disableGroupBuy(productId) {
    try { await mpPost(`/products/${productId}/group-buy/disable`) } catch (e) { error.value = e.message; throw e }
  }

  return {
    product, products, loading, error,
    create, get, getByListing, getByShop, getActiveByShop, update,
    updatePartial, remove, activate, pause, archive,
    addMedia, listMedia, removeMedia,
    createVariant, listVariants, updateVariant, removeVariant,
    createPriceRule, listPriceRules, updatePriceRule, removePriceRule,
    enableNegotiation, disableNegotiation, enableGroupBuy, disableGroupBuy,
    sellerProfile, myListings, createListing, updateListing,
    submitListing, updateGroupMarketplaceListing,
  }
})
